import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useSelector } from "react-redux";
import { faPhoneFlip, faQuestion } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Icon, Popper } from "@material-ui/core";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { metaDataService } from "../../../api";
import { AppState } from "../../../redux";
import "./SupportButton.scss";
import { getCurrentLanguage } from "../../../models/enums/salutationType.enum";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { t } from "i18next";

/**
 * SupportButton component
 *
 * This component is responsible for rendering the support button and the support hotline popper.
 * return: JSX.Element | null
 */
const SupportButton: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const accessibility = useSelector((state: AppState) => state.accessibility);
  const { i18n } = useTranslation();

  const [featureFlags, setFeatureFlags] = useState({
    support_hotline_is_enabled: false,
  });

  const [metaData, setMetaData] = useState({
    support_hotline_german: "",
    support_hotline_english: "",
  });
  const [metaDataIsLoading, setMetaDataIsLoading] = useState(true);

  /**
   * Fetch Feature Flags (Is Support-Hotline active?)
   */
  useEffect(() => {
    const fetchFeatureFlag = async () => {
      try {
        const response = await axios.get("/api/feature-flags");
        setFeatureFlags({
          support_hotline_is_enabled: response.data.support_hotline_is_enabled,
        });
      } catch (e) {
        enqueueSnackbar(
          t("backendResponses.error.captureNotSaved", { ns: "snackbars" }),
          {
            variant: "error",
          }
        );
      }
    };
    fetchFeatureFlag();
  }, []);

  /**
   * Fetch metadata from the API and set the support hotline content.
   */
  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        await i18n.changeLanguage(i18n.language || "de");
        const response = await metaDataService.getMetaData();

        setMetaData({
          support_hotline_german: response?.support_hotline_german || "",
          support_hotline_english: response?.support_hotline_english || "",
        });
      } catch (error) {
        enqueueSnackbar("Fehler beim Abrufen der Meta-Daten", { variant: "error" });
      } finally {
        setMetaDataIsLoading(false);
      }
    };

    fetchMetaData();
  }, [i18n.language]);

  /**
   * Handle the click event on the support button.
   * @param newPlacement: PopperPlacementType
   * @param event: React.MouseEvent<HTMLButtonElement>
   * return: void
   */
  const handleClick = () => (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  /**
   * Support hotline text
   */
  const supportText =
    getCurrentLanguage() === "de" || getCurrentLanguage() === "de-DE"
      ? metaData.support_hotline_german
      : metaData.support_hotline_english;

  const createSafeHTML = (htmlString: string) => {
    return { __html: DOMPurify.sanitize(htmlString) };
  };

  /**
   * Support button content
   *
   * If the metadata is still loading, display a loading spinner.
   * Otherwise, display the support hotline content.
   */
  const supportContent = metaDataIsLoading ? (
    <Box display="flex" justifyContent="center" alignItems="center" height="100px">
      <CircularProgress />
    </Box>
  ) : (
    <Box className="support-content" style={{ overflowWrap: "anywhere" }}>
      {/* eslint-disable-next-line react/no-danger */}
      <p dangerouslySetInnerHTML={createSafeHTML(supportText)} />
    </Box>
  );

  /**
   * Render the support button and the support hotline popper.
   * If the support hotline is not enabled, return null.
   */
  return featureFlags.support_hotline_is_enabled &&
    (metaData.support_hotline_german || metaData.support_hotline_english) ? (
    <>
      <Box
        className="support-button"
        style={{
          backgroundColor: accessibility.monoMode
            ? "#000"
            : process.env.REACT_APP_PRIMARY_COLOR || "#2FA79E",
        }}
        onClick={handleClick()}
      >
        <Icon className="support-icon">
          <FontAwesomeIcon icon={faQuestion} />
        </Icon>
      </Box>

      <Popper
        className="support-popper"
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
      >
        {() => (
          <>
            <Box
              className="support-popper-close-button"
              component="button"
              onClick={handleClick()}
            >
              <CloseRoundedIcon />
            </Box>
            {supportContent}
          </>
        )}
      </Popper>
    </>
  ) : null;
};

export default SupportButton;
